import { useEffect, useState, useMemo } from "react";
import { api, InventoryRow } from "../api";
import { formatVND, formatNumber } from "../utils";
import { GlassPanel, KpiCard, Modal, DataTable, Column } from "../components";

// ── Types ──────────────────────────────────────────────
interface Allocation {
  own_stock: number;
  consignment: number;
  demo_stock: number;
  defect_stock: number;
}

type AllocMap = Record<string, Allocation>;
type FilterMode = "all" | "allocated" | "unallocated";

const STORAGE_KEY = "inventory_allocation_v1";

function loadAllocations(): AllocMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveAllocations(map: AllocMap) {
  // TODO: API /api/inventory/allocation
  localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

// ── Stacked Progress Bar ───────────────────────────────
function StockProgressBar({ alloc, total }: { alloc: Allocation; total: number }) {
  if (total <= 0) return <div className="stock-bar empty" />;
  const segments = [
    { key: "own", value: alloc.own_stock, color: "#22c55e" },
    { key: "consign", value: alloc.consignment, color: "#3b82f6" },
    { key: "demo", value: alloc.demo_stock, color: "#f59e0b" },
    { key: "defect", value: alloc.defect_stock, color: "#ef4444" },
  ];
  return (
    <div className="stock-bar" title={`Mình mua: ${alloc.own_stock} | Ký gửi: ${alloc.consignment} | Demo: ${alloc.demo_stock} | Lỗi: ${alloc.defect_stock}`}>
      {segments.map((s) =>
        s.value > 0 ? (
          <div
            key={s.key}
            className="stock-bar-seg"
            style={{ width: `${(s.value / total) * 100}%`, backgroundColor: s.color }}
          />
        ) : null,
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────
export default function InventoryAllocationPage() {
  const [rows, setRows] = useState<InventoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allocMap, setAllocMap] = useState<AllocMap>(loadAllocations);
  const [filter, setFilter] = useState<FilterMode>("all");

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<InventoryRow | null>(null);
  const [form, setForm] = useState<Allocation>({ own_stock: 0, consignment: 0, demo_stock: 0, defect_stock: 0 });
  const [formError, setFormError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.inventory({ limit: 500 })
      .then((res) => { if (alive) setRows(res.inventory); })
      .catch((e) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // ── Filtered rows ──
  const filteredRows = useMemo(() => {
    if (filter === "all") return rows;
    if (filter === "allocated") return rows.filter((r) => allocMap[r.variant_id]);
    return rows.filter((r) => !allocMap[r.variant_id]);
  }, [rows, filter, allocMap]);

  // ── KPI ──
  const kpis = useMemo(() => {
    let ownValue = 0;
    let consignValue = 0;
    let allocatedCount = 0;
    for (const r of rows) {
      const a = allocMap[r.variant_id];
      if (!a) continue;
      allocatedCount++;
      const cost = (r as any).cost ?? r.default_price;
      ownValue += a.own_stock * cost;
      consignValue += a.consignment * cost;
    }
    return { ownValue, consignValue, allocatedCount };
  }, [rows, allocMap]);

  // ── Modal handlers ──
  const openModal = (row: InventoryRow) => {
    setEditingRow(row);
    const existing = allocMap[row.variant_id];
    setForm(existing || { own_stock: row.in_stock, consignment: 0, demo_stock: 0, defect_stock: 0 });
    setFormError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRow(null);
  };

  const saveAllocation = () => {
    if (!editingRow) return;
    const sum = form.own_stock + form.consignment + form.demo_stock + form.defect_stock;
    if (sum !== editingRow.in_stock) {
      setFormError(`Tổng phân loại (${formatNumber(sum)}) phải bằng tồn kho (${formatNumber(editingRow.in_stock)})`);
      return;
    }
    const next = { ...allocMap, [editingRow.variant_id]: { ...form } };
    setAllocMap(next);
    saveAllocations(next);
    closeModal();
  };

  const handleInput = (field: keyof Allocation, val: string) => {
    const n = Math.max(0, parseInt(val) || 0);
    setForm((f) => ({ ...f, [field]: n }));
    setFormError("");
  };

  // ── Table columns ──
  const columns: Column<InventoryRow>[] = [
    {
      key: "name",
      header: "Sản phẩm",
      render: (r) => (
        <div>
          <strong>{r.item_name}</strong>
          <div style={{ fontSize: 12, opacity: 0.6 }}>{r.sku || "—"}</div>
        </div>
      ),
    },
    { key: "total", header: "Tồn tổng", render: (r) => formatNumber(r.in_stock) },
    {
      key: "own",
      header: "Mình mua",
      render: (r) => {
        const a = allocMap[r.variant_id];
        return a ? formatNumber(a.own_stock) : "—";
      },
    },
    {
      key: "consignment",
      header: "Ký gửi",
      render: (r) => {
        const a = allocMap[r.variant_id];
        return a ? formatNumber(a.consignment) : "—";
      },
    },
    {
      key: "demo",
      header: "Demo",
      render: (r) => {
        const a = allocMap[r.variant_id];
        return a ? formatNumber(a.demo_stock) : "—";
      },
    },
    {
      key: "defect",
      header: "Lỗi",
      render: (r) => {
        const a = allocMap[r.variant_id];
        return a ? formatNumber(a.defect_stock) : "—";
      },
    },
    {
      key: "bar",
      header: "Phân bổ",
      render: (r) => {
        const a = allocMap[r.variant_id];
        return a ? <StockProgressBar alloc={a} total={r.in_stock} /> : <span style={{ opacity: 0.3 }}>—</span>;
      },
    },
    {
      key: "action",
      header: "",
      render: (r) => (
        <button className="btn btn-sm" onClick={() => openModal(r)}>
          Chia
        </button>
      ),
    },
  ];

  // ── Render ──
  return (
    <>
      {/* KPI Cards */}
      <div className="kpi-grid">
        <KpiCard
          label="Giá trị MÌNH MUA"
          value={formatVND(kpis.ownValue)}
          note="tính COGS"
          dotColor="ok"
        />
        <KpiCard
          label="Giá trị KÝ GỬI"
          value={formatVND(kpis.consignValue)}
          note="chỉ tính commission"
          dotColor="blue"
        />
        <KpiCard
          label="SP đã phân loại"
          value={formatNumber(kpis.allocatedCount)}
          note={`/ ${formatNumber(rows.length)} tổng`}
          dotColor={kpis.allocatedCount > 0 ? "ok" : "warn"}
        />
      </div>

      {/* Main Table */}
      <GlassPanel
        title="Phân loại tồn kho"
        badge={`${filteredRows.length} sản phẩm`}
        badgeVariant="info"
      >
        {/* Filter tabs */}
        <div className="tabs" style={{ marginBottom: 16 }}>
          <div className={`tab-item ${filter === "all" ? "active" : ""}`} onClick={() => setFilter("all")}>
            Tất cả
          </div>
          <div className={`tab-item ${filter === "allocated" ? "active" : ""}`} onClick={() => setFilter("allocated")}>
            Đã chia
          </div>
          <div className={`tab-item ${filter === "unallocated" ? "active" : ""}`} onClick={() => setFilter("unallocated")}>
            Chưa chia
          </div>
        </div>

        {loading ? (
          <div className="loading">Đang tải…</div>
        ) : error ? (
          <div className="error">⚠️ {error}</div>
        ) : filteredRows.length === 0 && filter === "all" ? (
          <div className="empty">Chưa có phân loại tồn kho. Bấm Chia trên SP để bắt đầu.</div>
        ) : filteredRows.length === 0 ? (
          <div className="empty">Không có sản phẩm nào trong bộ lọc này.</div>
        ) : (
          <DataTable
            columns={columns}
            rows={filteredRows}
            rowKey={(r) => r.variant_id}
            emptyMessage="Không có dữ liệu"
          />
        )}
      </GlassPanel>

      {/* Allocation Modal */}
      <Modal
        open={modalOpen}
        onClose={closeModal}
        title="Phân loại tồn kho"
        width={480}
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-ghost" onClick={closeModal}>Hủy</button>
            <button className="btn btn-primary" onClick={saveAllocation}>Lưu phân loại</button>
          </div>
        }
      >
        {editingRow && (
          <div className="alloc-form">
            <div className="alloc-form-header">
              <strong>{editingRow.item_name}</strong>
              <span style={{ opacity: 0.6 }}>Tồn tổng: <b>{formatNumber(editingRow.in_stock)}</b></span>
            </div>

            <div className="alloc-form-grid">
              <label>
                <span className="alloc-dot" style={{ background: "#22c55e" }} />
                Mình mua (own_stock)
                <input
                  type="number"
                  min={0}
                  value={form.own_stock}
                  onChange={(e) => handleInput("own_stock", e.target.value)}
                />
              </label>
              <label>
                <span className="alloc-dot" style={{ background: "#3b82f6" }} />
                Ký gửi (consignment)
                <input
                  type="number"
                  min={0}
                  value={form.consignment}
                  onChange={(e) => handleInput("consignment", e.target.value)}
                />
              </label>
              <label>
                <span className="alloc-dot" style={{ background: "#f59e0b" }} />
                Demo (demo_stock)
                <input
                  type="number"
                  min={0}
                  value={form.demo_stock}
                  onChange={(e) => handleInput("demo_stock", e.target.value)}
                />
              </label>
              <label>
                <span className="alloc-dot" style={{ background: "#ef4444" }} />
                Lỗi (defect_stock)
                <input
                  type="number"
                  min={0}
                  value={form.defect_stock}
                  onChange={(e) => handleInput("defect_stock", e.target.value)}
                />
              </label>
            </div>

            {/* Sum validation */}
            <div className={`alloc-sum ${(form.own_stock + form.consignment + form.demo_stock + form.defect_stock) !== editingRow.in_stock ? "alloc-sum-error" : ""}`}>
              Tổng: {formatNumber(form.own_stock + form.consignment + form.demo_stock + form.defect_stock)} / {formatNumber(editingRow.in_stock)}
              {(form.own_stock + form.consignment + form.demo_stock + form.defect_stock) === editingRow.in_stock && " ✓"}
            </div>

            {formError && <div className="alloc-error">{formError}</div>}
          </div>
        )}
      </Modal>
    </>
  );
}
